import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Badge } from 'react-bootstrap';
import {
  useGetMarketingAdminLibraryQuery,
  useCreateMarketingCategoryMutation,
  useUpdateMarketingCategoryMutation,
  useDeleteMarketingCategoryMutation,
  useReorderMarketingCategoriesMutation,
  useCreateMarketingGroupMutation,
  useUpdateMarketingGroupMutation,
  useDeleteMarketingGroupMutation,
  useReorderMarketingGroupsMutation,
  useCreateMarketingLinkMutation,
  useCreateMarketingHeadingMutation,
  useGetMarketingUploadTicketMutation,
  useCommitMarketingUploadMutation,
  useUpdateMarketingResourceMutation,
  useDeleteMarketingResourceMutation,
  useReorderMarketingResourcesMutation,
  useGetMarketingResourceUrlMutation,
} from '../../app/api';
// The shared employee-admin styling kit. It lives under entrepreneur/ for
// historical reasons; AdminPage is the same shell with a section label, so
// nothing here reads as though Marketing sits under Entrepreneur.
import { brand, cardStyle, headStyle, AdminPage } from '../entrepreneur/common';
import c2pCentralLoginLink from '../../components/c2pCentralLink';

/* Marketing Resources — the library published to every B2B entrepreneur firm.
 *
 * Three levels, matching the source pages Marketing publishes:
 *
 *   category  a section of the portal's Marketing Resources menu
 *   group     one topic inside it — a guide, a presentation, a month, a
 *             discount partner
 *   resource  one asset: a file in `pca-marketing`, an external link, or a
 *             plain HEADING that sub-divides a long group
 *
 * Global — one library, every firm sees the same sections (the same model as
 * B2B Announcements).
 *
 * A category's FOLDER in S3 is fixed when it is created and shown here as
 * read-only next to the name, because that is the one thing on this page a
 * rename cannot change: renaming "Guides" leaves its objects under `guides/`.
 * Showing the folder is what stops that being a surprise later.
 *
 * GROUPS OWN NO FOLDER. Only categories do. That is why an item can be moved
 * between groups freely (it is a heading change and nothing else) while moving
 * a FILE between categories is refused — its key encodes the category prefix.
 *
 * Files go browser → S3 directly (a presigned POST ticket from the API), never
 * through the API, so a 90 MB deck is not blocked by the 26 MB request-body cap
 * on the nginx in front of us.
 */

// Bootstrap icons offered for a category. A short curated list rather than a
// free-text field — the icon shows up in the members' portal, so it should be
// one of a handful that look right there.
const ICON_CHOICES = [
  { value: 'file-earmark-text',   label: 'Document' },
  { value: 'file-earmark-slides', label: 'Presentation' },
  { value: 'book',                label: 'Book / guide' },
  { value: 'tag',                 label: 'Tag / discount' },
  { value: 'megaphone',           label: 'Megaphone' },
  { value: 'images',              label: 'Images' },
  { value: 'play-btn',            label: 'Video' },
  { value: 'palette',            label: 'Brand / design' },
  { value: 'envelope-paper',      label: 'Mailer' },
  { value: 'graph-up-arrow',      label: 'Growth' },
];

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};
const areaStyle = { ...inputStyle, minHeight: 90, fontFamily: 'inherit', lineHeight: 1.45 };
const btnPrimary = {
  background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
  padding: '6px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
};
const btnPlain = {
  background: 'white', color: brand.charcoal, border: `1px solid ${brand.softGray}`,
  borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
};
const btnTiny = { ...btnPlain, padding: '2px 8px', fontSize: '0.75rem' };

// Shown under every rich-text box. The renderer on both portals supports exactly
// these four things, so saying so beats letting someone discover that a heading
// or a table comes out as literal text.
const RICH_TEXT_HINT = 'Blank line = new paragraph · "- " starts a bullet · '
  + '[text](https://…) makes a link · **bold**';

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const errText = (e, fallback) => e?.data?.detail || e?.detail || e?.message || fallback;

/* Send the bytes straight to S3 with the ticket the API minted. The presigned
 * POST's own conditions (bucket, key, content type, size range) are what enforce
 * the rules — this function just assembles the form in the order S3 requires:
 * every field first, `file` LAST. */
async function putToS3(ticket, file) {
  const form = new FormData();
  Object.entries(ticket.fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file);
  const res = await fetch(ticket.url, { method: 'POST', body: form });
  if (!res.ok) {
    // S3 answers with an XML error body; its <Message> is the useful part and
    // the status alone ("403") tells the user nothing actionable.
    const body = await res.text().catch(() => '');
    const message = /<Message>([^<]+)<\/Message>/.exec(body)?.[1];
    throw new Error(message || `Upload failed (HTTP ${res.status}).`);
  }
}

// Reorder helper shared by categories, groups and resources: swap with a
// neighbour and send the WHOLE list back, so the server writes evenly-spaced
// positions rather than us guessing at them.
const swapped = (ids, id, delta) => {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

function CategoryForm({ draft, onChange }) {
  const set = (k, v) => onChange({ ...draft, [k]: v });
  return (
    <>
      <input style={{ ...inputStyle, marginBottom: '0.5rem' }} placeholder="Category name"
        value={draft.name} onChange={(e) => set('name', e.target.value)} />
      <input style={{ ...inputStyle, marginBottom: '0.5rem' }} placeholder="Short description (optional)"
        value={draft.description} onChange={(e) => set('description', e.target.value)} />
      <textarea style={{ ...areaStyle, marginBottom: '0.2rem' }}
        placeholder="Intro shown above the groups (optional) — e.g. What's included each month"
        value={draft.intro} onChange={(e) => set('intro', e.target.value)} />
      <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginBottom: '0.5rem' }}>{RICH_TEXT_HINT}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Icon:
          <select style={{ ...inputStyle, width: 'auto' }} value={draft.icon}
            onChange={(e) => set('icon', e.target.value)}>
            <option value="">None</option>
            {ICON_CHOICES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          {draft.icon && <i className={`bi bi-${draft.icon}`} style={{ color: brand.goldenrod, fontSize: '1.1rem' }} />}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.published} onChange={(e) => set('published', e.target.checked)} />
          Visible to firms
        </label>
        {/* Off suits a section whose body copy IS the content (Member
            Discounts); on suits a long list someone scans (Presentations). */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.collapsible} onChange={(e) => set('collapsible', e.target.checked)} />
          Collapse groups by default
        </label>
        {/* A-Z suits a reference list someone arrives at knowing what they
            want (Guides). Manual suits an order that carries meaning — the
            Toolkit is newest-month-first, Presentations is curated. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Group order:
          <select style={{ ...inputStyle, width: 'auto' }} value={draft.group_sort}
            onChange={(e) => set('group_sort', e.target.value)}>
            <option value="MANUAL">Manual (up/down arrows)</option>
            <option value="ALPHA">Alphabetical (A–Z)</option>
          </select>
        </label>
      </div>
      {draft.group_sort === 'ALPHA' && (
        <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.4rem' }}>
          <i className="bi bi-info-circle me-1" />
          Groups sort by title, so new ones land in the right place on their own.
          The up/down arrows are hidden while this is on.
        </div>
      )}
    </>
  );
}
CategoryForm.propTypes = { draft: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired };

/* One item. Files show their size and a "Download" that mints a presigned URL;
 * links show (and can change) their URL; headings are just a label. */
function ResourceRow({ resource, groups, first, last, onMove, onSaved, onError }) {
  const [update, updateState] = useUpdateMarketingResourceMutation();
  const [remove] = useDeleteMarketingResourceMutation();
  const [getUrl] = useGetMarketingResourceUrlMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: resource.title || '',
    description: resource.description || '',
    url: resource.url || '',
  });

  const isLink = resource.kind === 'LINK';
  const isHeading = resource.kind === 'HEADING';

  const save = async () => {
    const body = { title: draft.title.trim() };
    if (!isHeading) body.description = draft.description || null;
    if (isLink) body.url = draft.url.trim();
    try {
      await update({ id: resource.id, body }).unwrap();
      setEditing(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not save that item.')); }
  };

  const togglePublished = async () => {
    try {
      await update({ id: resource.id, body: { published: !resource.published } }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not change visibility.')); }
  };

  // Re-filing is a plain PATCH even for a file: a group owns no S3 prefix, so
  // the object does not move and nothing has to be re-uploaded.
  const moveToGroup = async (value) => {
    try {
      await update({ id: resource.id, body: { group_id: value ? Number(value) : null } }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not move that item.')); }
  };

  const del = async () => {
    const what = isHeading ? 'heading' : (isLink ? 'link' : 'file (it will be removed from S3)');
    if (!window.confirm(`Delete "${resource.title}"? This deletes the ${what} and cannot be undone.`)) return;
    try {
      await remove({ id: resource.id }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not delete that item.')); }
  };

  const download = async () => {
    try {
      const res = await getUrl({ id: resource.id }).unwrap();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) { onError(errText(e, 'Could not open that file.')); }
  };

  const kindIcon = isHeading ? 'type-h3' : (isLink ? 'link-45deg' : 'file-earmark-arrow-down');

  return (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0', borderTop: `1px solid ${brand.softGray}` }}>
      {/* Reorder handles. The order set here is the order members see. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
        <button type="button" style={{ ...btnTiny, opacity: first ? 0.35 : 1 }} disabled={first}
          onClick={() => onMove(resource.id, -1)} aria-label="Move up" title="Move up">
          <i className="bi bi-chevron-up" />
        </button>
        <button type="button" style={{ ...btnTiny, opacity: last ? 0.35 : 1 }} disabled={last}
          onClick={() => onMove(resource.id, 1)} aria-label="Move down" title="Move down">
          <i className="bi bi-chevron-down" />
        </button>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <>
            <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Title"
              value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            {!isHeading && (
              <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Description (optional)"
                value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            )}
            {isLink && (
              <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="https://…"
                value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
            )}
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button type="button" style={btnPrimary} onClick={save}
                disabled={updateState.isLoading || !draft.title.trim()}>
                {updateState.isLoading ? 'Saving…' : 'Save'}
              </button>
              <button type="button" style={btnPlain} onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
              <i className={`bi bi-${kindIcon}`} style={{ color: brand.tealBlue }} />
              <span style={{ fontWeight: 700, color: brand.deepBlue }}>{resource.title}</span>
              {!resource.published && <Badge bg="secondary" style={{ fontWeight: 600 }}>Hidden</Badge>}
              <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
                {isHeading ? 'Heading' : (isLink ? 'Link'
                  : [resource.filename, fmtSize(resource.content_size)].filter(Boolean).join(' · '))}
              </span>
            </div>
            {resource.description && (
              <div style={{ color: brand.charcoal, fontSize: '0.82rem', marginTop: '0.2rem' }}>{resource.description}</div>
            )}
            {isLink && (
              <a href={c2pCentralLoginLink(resource.url)} target="_blank" rel="noopener noreferrer"
                style={{ color: brand.tealBlue, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                {resource.url}
              </a>
            )}
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" style={btnTiny} onClick={() => setEditing(true)}>Edit</button>
              <button type="button" style={btnTiny} onClick={togglePublished}>
                {resource.published ? 'Hide from firms' : 'Show to firms'}
              </button>
              {!isLink && !isHeading && <button type="button" style={btnTiny} onClick={download}>Download</button>}
              <select style={{ ...btnTiny, padding: '2px 6px' }} value={resource.group_id || ''}
                aria-label="Move to group" title="Move to another group"
                onChange={(e) => moveToGroup(e.target.value)}>
                <option value="">— Section (ungrouped)</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </select>
              <button type="button" style={{ ...btnTiny, color: brand.red, borderColor: brand.red }} onClick={del}>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
ResourceRow.propTypes = {
  resource: PropTypes.object.isRequired,
  groups: PropTypes.array,
  first: PropTypes.bool,
  last: PropTypes.bool,
  onMove: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
ResourceRow.defaultProps = { groups: [] };

/* The "add something here" panel. File, link and heading are three forms behind
 * one toggle rather than one form with a mode field, because they share only the
 * title and merging them made every field read as optional.
 *
 * `groupId` is null when adding to the section itself rather than to a group. */
function AddResource({ category, groupId, onSaved, onError }) {
  const [mode, setMode] = useState(null);      // null | 'file' | 'link' | 'heading'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const [createLink] = useCreateMarketingLinkMutation();
  const [createHeading] = useCreateMarketingHeadingMutation();
  const [getTicket] = useGetMarketingUploadTicketMutation();
  const [commit] = useCommitMarketingUploadMutation();

  const where = groupId ? '' : ' to the section itself';

  const reset = () => {
    setMode(null); setTitle(''); setDescription(''); setUrl('');
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submitLink = async () => {
    setBusy(true);
    try {
      await createLink({
        category_id: category.id, group_id: groupId, title: title.trim(),
        url: url.trim(), description: description || null,
      }).unwrap();
      reset();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not add that link.')); }
    finally { setBusy(false); }
  };

  const submitHeading = async () => {
    setBusy(true);
    try {
      await createHeading({
        category_id: category.id, group_id: groupId, title: title.trim(),
      }).unwrap();
      reset();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not add that heading.')); }
    finally { setBusy(false); }
  };

  const submitFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { onError('Choose a file first.'); return; }
    setBusy(true);
    try {
      setProgress('Preparing…');
      const ticket = await getTicket({
        category_id: category.id, filename: file.name,
        size_bytes: file.size, content_type: file.type || null,
      }).unwrap();

      setProgress(`Uploading ${fmtSize(file.size)}…`);
      await putToS3(ticket, file);

      // Only now does a row exist. If commit fails the object is orphaned in S3
      // rather than catalogued — the message says so, because the fix is to try
      // again (a second upload makes a new key) not to hunt for a half-made row.
      setProgress('Saving…');
      await commit({
        category_id: category.id, group_id: groupId, s3_key: ticket.s3_key,
        title: (title.trim() || file.name), filename: file.name,
        description: description || null, content_type: ticket.content_type,
      }).unwrap();
      reset();
      await onSaved();
    } catch (e) {
      onError(errText(e, 'Upload failed.'));
      setProgress(null);
    } finally { setBusy(false); }
  };

  if (!mode) {
    return (
      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
        <button type="button" style={btnPlain} onClick={() => setMode('file')}>
          <i className="bi bi-upload me-1" />Upload file
        </button>
        <button type="button" style={btnPlain} onClick={() => setMode('link')}>
          <i className="bi bi-link-45deg me-1" />Add link
        </button>
        {/* Only inside a group: a sub-divider at the top of a section, with
            nothing above it to divide, is just a stray label. */}
        {groupId && (
          <button type="button" style={btnPlain} onClick={() => setMode('heading')}>
            <i className="bi bi-type-h3 me-1" />Add heading
          </button>
        )}
      </div>
    );
  }

  const submit = { file: submitFile, link: submitLink, heading: submitHeading }[mode];
  const disabled = busy
    || (mode === 'link' && (!title.trim() || !url.trim()))
    || (mode === 'heading' && !title.trim());

  return (
    <div style={{ background: '#fbfaf8', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.8rem', marginTop: '0.6rem' }}>
      <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        {mode === 'file' && `Upload a file${where}`}
        {mode === 'link' && `Add a link${where}`}
        {mode === 'heading' && 'Add a heading'}
      </div>
      {mode === 'file' && (
        <input ref={fileRef} type="file" style={{ ...inputStyle, marginBottom: '0.4rem', padding: '5px 8px' }} />
      )}
      <input style={{ ...inputStyle, marginBottom: '0.4rem' }}
        placeholder={mode === 'file' ? 'Title (defaults to the file name)' : 'Title'}
        value={title} onChange={(e) => setTitle(e.target.value)} />
      {mode !== 'heading' && (
        <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Description (optional)"
          value={description} onChange={(e) => setDescription(e.target.value)} />
      )}
      {mode === 'link' && (
        <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="https://…"
          value={url} onChange={(e) => setUrl(e.target.value)} />
      )}
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={btnPrimary} disabled={disabled} onClick={submit}>
          {busy ? (progress || 'Working…')
            : { file: 'Upload', link: 'Add link', heading: 'Add heading' }[mode]}
        </button>
        <button type="button" style={btnPlain} onClick={reset} disabled={busy}>Cancel</button>
        {mode === 'file' && !busy && (
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            Goes to <code>pca-marketing/{category.slug}/</code>
          </span>
        )}
        {mode === 'heading' && (
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            A label row — it opens nothing.
          </span>
        )}
      </div>
    </div>
  );
}
AddResource.propTypes = {
  category: PropTypes.object.isRequired,
  groupId: PropTypes.number,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
AddResource.defaultProps = { groupId: null };

const emptyGroup = { title: '', subtitle: '', url: '', body: '', badge: '', published: true };

function GroupForm({ draft, onChange }) {
  const set = (k, v) => onChange({ ...draft, [k]: v });
  return (
    <>
      <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Title (the guide, presentation, month or partner)"
        value={draft.title} onChange={(e) => set('title', e.target.value)} />
      <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Subtitle (optional) — e.g. the month's theme"
        value={draft.subtitle} onChange={(e) => set('subtitle', e.target.value)} />
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
        <input style={{ ...inputStyle, flex: 3, minWidth: 200 }}
          placeholder="Link for the heading itself (optional) — e.g. the guide PDF"
          value={draft.url} onChange={(e) => set('url', e.target.value)} />
        <input style={{ ...inputStyle, flex: 1, minWidth: 120 }} placeholder="Badge — e.g. C2P15"
          value={draft.badge} onChange={(e) => set('badge', e.target.value)} />
      </div>
      <textarea style={{ ...areaStyle, marginBottom: '0.2rem' }}
        placeholder="Body copy (optional) — the partner write-up, pricing bullets, a contact"
        value={draft.body} onChange={(e) => set('body', e.target.value)} />
      <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginBottom: '0.4rem' }}>{RICH_TEXT_HINT}</div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!draft.published} onChange={(e) => set('published', e.target.checked)} />
        Visible to firms
      </label>
    </>
  );
}
GroupForm.propTypes = { draft: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired };

function GroupCard({ category, group, siblings, first, last, sortable, onMoveGroup, onSaved, onError }) {
  const [update, updateState] = useUpdateMarketingGroupMutation();
  const [remove] = useDeleteMarketingGroupMutation();
  const [reorderResources] = useReorderMarketingResourcesMutation();
  const [editing, setEditing] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [draft, setDraft] = useState({
    title: group.title || '', subtitle: group.subtitle || '', url: group.url || '',
    body: group.body || '', badge: group.badge || '', published: !!group.published,
  });

  const resources = group.resources || [];

  const save = async () => {
    try {
      await update({
        id: group.id,
        body: {
          title: draft.title.trim(), subtitle: draft.subtitle || null,
          url: draft.url || null, body: draft.body || null,
          badge: draft.badge || null, published: !!draft.published,
        },
      }).unwrap();
      setEditing(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not save that group.')); }
  };

  const del = async () => {
    const warning = resources.length
      ? `Delete the "${group.title}" group? Its ${resources.length} item`
        + `${resources.length === 1 ? '' : 's'} will move back to the top of the section, `
        + 'not be deleted.'
      : `Delete the "${group.title}" group?`;
    if (!window.confirm(warning)) return;
    try {
      await remove({ id: group.id }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not delete that group.')); }
  };

  const moveResource = async (resourceId, delta) => {
    const ids = swapped(resources.map((r) => r.id), resourceId, delta);
    if (!ids) return;
    try {
      await reorderResources({ ordered_ids: ids }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not reorder those items.')); }
  };

  return (
    <div style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, marginBottom: '0.6rem', background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', padding: '0.55rem 0.75rem', background: '#fbfaf8', borderBottom: collapsed ? 'none' : `1px solid ${brand.softGray}` }}>
        <button type="button" style={{ ...btnTiny, border: 'none', background: 'transparent' }}
          onClick={() => setCollapsed((v) => !v)} aria-expanded={!collapsed}
          title={collapsed ? 'Show items' : 'Hide items'}>
          <i className={`bi bi-chevron-${collapsed ? 'right' : 'down'}`} />
        </button>
        <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.88rem' }}>{group.title}</span>
        {group.badge && (
          <Badge bg="warning" text="dark" style={{ fontWeight: 600 }}>{group.badge}</Badge>
        )}
        <Badge bg="secondary" style={{ fontWeight: 600 }}>{resources.length}</Badge>
        {!group.published && <Badge bg="warning" text="dark" style={{ fontWeight: 600 }}>Hidden</Badge>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
          {/* Hidden entirely on an A–Z section: the server sorts by title there
              and ignores sort_order, so an arrow would appear to do nothing. */}
          {sortable && (
            <>
              <button type="button" style={{ ...btnTiny, opacity: first ? 0.35 : 1 }} disabled={first}
                onClick={() => onMoveGroup(group.id, -1)} aria-label="Move group up" title="Move up">
                <i className="bi bi-chevron-up" />
              </button>
              <button type="button" style={{ ...btnTiny, opacity: last ? 0.35 : 1 }} disabled={last}
                onClick={() => onMoveGroup(group.id, 1)} aria-label="Move group down" title="Move down">
                <i className="bi bi-chevron-down" />
              </button>
            </>
          )}
          <button type="button" style={btnTiny} onClick={() => { setEditing((v) => !v); setCollapsed(false); }}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button type="button" style={{ ...btnTiny, color: brand.red, borderColor: brand.red }} onClick={del}>
            Delete
          </button>
        </div>
      </div>

      {!collapsed && (
        <div style={{ padding: '0.6rem 0.75rem 0.75rem' }}>
          {editing ? (
            <>
              <GroupForm draft={draft} onChange={setDraft} />
              <div style={{ marginTop: '0.6rem' }}>
                <button type="button" style={btnPrimary} onClick={save}
                  disabled={updateState.isLoading || !draft.title.trim()}>
                  {updateState.isLoading ? 'Saving…' : 'Save group'}
                </button>
              </div>
            </>
          ) : (
            <>
              {group.subtitle && (
                <div style={{ color: brand.charcoal, fontSize: '0.82rem' }}>{group.subtitle}</div>
              )}
              {group.url && (
                <a href={c2pCentralLoginLink(group.url)} target="_blank" rel="noopener noreferrer"
                  style={{ color: brand.tealBlue, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  {group.url}
                </a>
              )}
              {group.body && (
                <pre style={{
                  whiteSpace: 'pre-wrap', color: brand.charcoal, fontSize: '0.78rem',
                  background: '#fbfaf8', border: `1px solid ${brand.softGray}`, borderRadius: 6,
                  padding: '0.5rem', margin: '0.4rem 0 0', fontFamily: 'inherit',
                }}>
                  {group.body}
                </pre>
              )}
            </>
          )}

          {resources.map((r, i) => (
            <ResourceRow key={r.id} resource={r} groups={siblings}
              first={i === 0} last={i === resources.length - 1}
              onMove={moveResource} onSaved={onSaved} onError={onError} />
          ))}

          <AddResource category={category} groupId={group.id} onSaved={onSaved} onError={onError} />
        </div>
      )}
    </div>
  );
}
GroupCard.propTypes = {
  category: PropTypes.object.isRequired,
  group: PropTypes.object.isRequired,
  siblings: PropTypes.array,
  first: PropTypes.bool,
  last: PropTypes.bool,
  sortable: PropTypes.bool,
  onMoveGroup: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
GroupCard.defaultProps = { siblings: [], sortable: true };

function CategoryCard({ category, first, last, onMoveCategory, onSaved, onError }) {
  const [update, updateState] = useUpdateMarketingCategoryMutation();
  const [remove] = useDeleteMarketingCategoryMutation();
  const [createGroup, createGroupState] = useCreateMarketingGroupMutation();
  const [reorderGroups] = useReorderMarketingGroupsMutation();
  const [reorderResources] = useReorderMarketingResourcesMutation();
  const [editing, setEditing] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroup, setNewGroup] = useState(emptyGroup);
  const [draft, setDraft] = useState({
    name: category.name || '',
    description: category.description || '',
    intro: category.intro || '',
    icon: category.icon || '',
    published: !!category.published,
    collapsible: category.collapsible !== false,
    group_sort: category.group_sort || 'MANUAL',
  });

  const groups = category.groups || [];
  // The server already returned them in A–Z order for an ALPHA section; this
  // only decides whether the manual arrows are offered.
  const alphabetical = category.group_sort === 'ALPHA';
  const loose = category.resources || [];
  const itemCount = loose.length + groups.reduce((n, g) => n + (g.resources?.length || 0), 0);

  const save = async () => {
    try {
      await update({
        id: category.id,
        body: {
          name: draft.name.trim(), description: draft.description || null,
          intro: draft.intro || null, icon: draft.icon || null,
          published: !!draft.published, collapsible: !!draft.collapsible,
          group_sort: draft.group_sort,
        },
      }).unwrap();
      setEditing(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not save that category.')); }
  };

  const del = async () => {
    if (!window.confirm(`Delete the "${category.name}" category?`)) return;
    try {
      await remove({ id: category.id }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not delete that category.')); }
  };

  const submitGroup = async () => {
    try {
      await createGroup({
        category_id: category.id, title: newGroup.title.trim(),
        subtitle: newGroup.subtitle || null, url: newGroup.url || null,
        body: newGroup.body || null, badge: newGroup.badge || null,
        published: !!newGroup.published,
      }).unwrap();
      setNewGroup(emptyGroup);
      setShowNewGroup(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not create that group.')); }
  };

  const moveGroup = async (groupId, delta) => {
    const ids = swapped(groups.map((g) => g.id), groupId, delta);
    if (!ids) return;
    try {
      await reorderGroups({ ordered_ids: ids }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not reorder those groups.')); }
  };

  const moveLoose = async (resourceId, delta) => {
    const ids = swapped(loose.map((r) => r.id), resourceId, delta);
    if (!ids) return;
    try {
      await reorderResources({ ordered_ids: ids }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not reorder those items.')); }
  };

  return (
    <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
      <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        {category.icon && <i className={`bi bi-${category.icon}`} style={{ color: brand.goldenrod }} />}
        <span>{category.name}</span>
        <Badge bg="secondary" style={{ fontWeight: 600 }}>
          {groups.length} group{groups.length === 1 ? '' : 's'} · {itemCount} item{itemCount === 1 ? '' : 's'}
        </Badge>
        {!category.published && <Badge bg="warning" text="dark" style={{ fontWeight: 600 }}>Hidden from firms</Badge>}
        {/* The S3 folder is fixed at creation and a rename won't move it, so it
            is shown rather than left to be discovered. */}
        <code style={{ color: brand.charcoal, fontSize: '0.72rem' }}>{category.slug}/</code>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
          <button type="button" style={{ ...btnTiny, opacity: first ? 0.35 : 1 }} disabled={first}
            onClick={() => onMoveCategory(category.id, -1)} aria-label="Move category up" title="Move up">
            <i className="bi bi-chevron-up" />
          </button>
          <button type="button" style={{ ...btnTiny, opacity: last ? 0.35 : 1 }} disabled={last}
            onClick={() => onMoveCategory(category.id, 1)} aria-label="Move category down" title="Move down">
            <i className="bi bi-chevron-down" />
          </button>
          <button type="button" style={btnTiny} onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button type="button" style={{ ...btnTiny, color: brand.red, borderColor: brand.red }} onClick={del}>
            Delete
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        {editing && (
          <div style={{ marginBottom: '0.8rem' }}>
            <CategoryForm draft={draft} onChange={setDraft} />
            <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem' }}>
              <button type="button" style={btnPrimary} onClick={save}
                disabled={updateState.isLoading || !draft.name.trim()}>
                {updateState.isLoading ? 'Saving…' : 'Save category'}
              </button>
            </div>
            <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.4rem' }}>
              <i className="bi bi-info-circle me-1" />
              Renaming changes what firms see. The S3 folder stays <code>{category.slug}/</code> —
              files already uploaded don&apos;t move.
            </div>
          </div>
        )}

        {category.description && !editing && (
          <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginBottom: '0.4rem' }}>{category.description}</div>
        )}

        {/* Items filed on the section itself rather than in a group. Firms see
            these above every group, so they are shown here in that position. */}
        {loose.length > 0 && (
          <div style={{ marginBottom: '0.8rem' }}>
            <div style={{ color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.7px', fontWeight: 700 }}>
              Section items — shown above the groups
            </div>
            {loose.map((r, i) => (
              <ResourceRow key={r.id} resource={r} groups={groups}
                first={i === 0} last={i === loose.length - 1}
                onMove={moveLoose} onSaved={onSaved} onError={onError} />
            ))}
          </div>
        )}

        {groups.length === 0 && loose.length === 0 && (
          <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Nothing in here yet. Firms see the section with an &quot;empty&quot; note until there is.
          </div>
        )}

        {alphabetical && groups.length > 0 && (
          <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginBottom: '0.4rem' }}>
            <i className="bi bi-sort-alpha-down me-1" />
            Firms see these groups A–Z. Edit the category to change the order mode.
          </div>
        )}

        {groups.map((g, i) => (
          <GroupCard key={g.id} category={category} group={g} siblings={groups}
            first={i === 0} last={i === groups.length - 1}
            sortable={!alphabetical}
            onMoveGroup={moveGroup} onSaved={onSaved} onError={onError} />
        ))}

        {showNewGroup ? (
          <div style={{ background: '#fbfaf8', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.8rem', marginTop: '0.4rem' }}>
            <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              New group in {category.name}
            </div>
            <GroupForm draft={newGroup} onChange={setNewGroup} />
            <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem' }}>
              <button type="button" style={btnPrimary} onClick={submitGroup}
                disabled={createGroupState.isLoading || !newGroup.title.trim()}>
                {createGroupState.isLoading ? 'Creating…' : 'Create group'}
              </button>
              <button type="button" style={btnPlain}
                onClick={() => { setShowNewGroup(false); setNewGroup(emptyGroup); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
            <button type="button" style={btnPlain} onClick={() => setShowNewGroup(true)}>
              <i className="bi bi-plus me-1" />New group
            </button>
            {/* Adding straight to the section is the exception, so it is not the
                prominent button — it is for standing items like the
                Presentation Library's instructions. */}
            <AddResource category={category} groupId={null} onSaved={onSaved} onError={onError} />
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
CategoryCard.propTypes = {
  category: PropTypes.object.isRequired,
  first: PropTypes.bool,
  last: PropTypes.bool,
  onMoveCategory: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

const emptyCategory = {
  name: '', description: '', intro: '', icon: '',
  published: true, collapsible: true, group_sort: 'MANUAL',
};

export default function MarketingResources() {
  const { data, isLoading, refetch } = useGetMarketingAdminLibraryQuery();
  const [createCategory, createState] = useCreateMarketingCategoryMutation();
  const [reorderCategories] = useReorderMarketingCategoriesMutation();

  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyCategory);
  const [error, setError] = useState(null);

  const categories = useMemo(() => data?.categories || [], [data]);
  const itemCount = categories.reduce((n, cat) => n + (cat.resource_count || 0), 0);
  const groupCount = categories.reduce((n, cat) => n + (cat.groups?.length || 0), 0);

  const submitNew = async () => {
    setError(null);
    try {
      await createCategory({
        name: newDraft.name.trim(), description: newDraft.description || null,
        intro: newDraft.intro || null, icon: newDraft.icon || null,
        published: !!newDraft.published, collapsible: !!newDraft.collapsible,
        group_sort: newDraft.group_sort,
      }).unwrap();
      setNewDraft(emptyCategory);
      setShowNew(false);
      await refetch();
    } catch (e) { setError(errText(e, 'Could not create that category.')); }
  };

  const moveCategory = async (categoryId, delta) => {
    const ids = swapped(categories.map((cat) => cat.id), categoryId, delta);
    if (!ids) return;
    setError(null);
    try {
      await reorderCategories({ ordered_ids: ids }).unwrap();
      await refetch();
    } catch (e) { setError(errText(e, 'Could not reorder those categories.')); }
  };

  return (
    <AdminPage
      section="Marketing"
      icon="megaphone"
      title="Marketing Resources"
      subtitle="The collateral library every entrepreneur firm sees in their hub."
      actions={
        <button type="button" style={btnPrimary} onClick={() => setShowNew((v) => !v)}>
          <i className={`bi ${showNew ? 'bi-x' : 'bi-plus'} me-1`} />
          {showNew ? 'Cancel' : 'New category'}
        </button>
      }
    >
      {error && (
        <div style={{ background: 'rgba(192,57,43,0.08)', border: `1px solid ${brand.red}`, color: brand.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}

      {showNew && (
        <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
          <Card.Header style={headStyle}>New category</Card.Header>
          <Card.Body>
            <CategoryForm draft={newDraft} onChange={setNewDraft} />
            <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnPrimary} onClick={submitNew}
                disabled={createState.isLoading || !newDraft.name.trim()}>
                {createState.isLoading ? 'Creating…' : 'Create'}
              </button>
              <button type="button" style={btnPlain} onClick={() => { setShowNew(false); setNewDraft(emptyCategory); }}>
                Cancel
              </button>
            </div>
            <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.5rem' }}>
              <i className="bi bi-info-circle me-1" />
              A category becomes its own folder in the <code>pca-marketing</code> bucket, named
              from the title. That folder name is set once and never changes, so pick the name
              with a little care.
            </div>
          </Card.Body>
        </Card>
      )}

      {isLoading && <Spinner size="sm" />}

      {!isLoading && categories.length === 0 && (
        <Card style={cardStyle}>
          <Card.Body style={{ color: brand.charcoal, fontSize: '0.87rem' }}>
            No categories yet. Create one to start the library — firms see the Marketing
            Resources menu only once there is something published in it.
          </Card.Body>
        </Card>
      )}

      {!isLoading && categories.length > 0 && (
        <div style={{ color: brand.charcoal, fontSize: '0.78rem', marginBottom: '0.6rem' }}>
          {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} · {groupCount} group
          {groupCount === 1 ? '' : 's'} · {itemCount} item{itemCount === 1 ? '' : 's'} · order here
          is the order firms see
        </div>
      )}

      {categories.map((cat, i) => (
        <CategoryCard key={cat.id} category={cat}
          first={i === 0} last={i === categories.length - 1}
          onMoveCategory={moveCategory}
          onSaved={refetch}
          onError={setError} />
      ))}
    </AdminPage>
  );
}
